import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { DEMO_MODE } from '@/lib/demoMode';
import { notifyAccountStatusChanged } from '@/lib/accountEvents';

export function useAccountReactivation() {
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const checkAndReactivateAccount = async () => {
      if (DEMO_MODE || !user) return;

      try {
        // Check if account is deactivated
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('account_status, deactivation_reason, first_name, last_name')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking account status:', error);
          return;
        }

        // An admin suspension (client trust flags, 20270315000000) is stored as
        // deactivated + deactivation_reason 'trust_flag:*'. It is NOT the
        // voluntary pause. Login cannot undo a staff suspension, and the
        // client deserves to be told what has happened to their account.
        if (
          profile?.account_status === 'deactivated'
          && typeof profile.deactivation_reason === 'string'
          && profile.deactivation_reason.startsWith('trust_flag:')
        ) {
          toast({
            title: 'Account suspended',
            description: 'Your account has been suspended and cannot make new bookings. Please contact support.',
            variant: 'destructive',
          });
          return;
        }

        if (profile?.account_status === 'deactivated') {
          // The Delete Account page promises "reactivate any time by logging
          // back in". reactivate_own_account (20270486000000) honours that for
          // a pause the owner made and refuses anything a moderator did, so
          // a login reverses exactly the pauses it should and nothing else.
          const { error: reactivateError } = await supabase.rpc('reactivate_own_account');
          if (reactivateError) {
            // Two tabs of the same session race here; the loser's RPC finds
            // the row already active. That is a success it did not perform,
            // not an account that needs support (Codex on #1300): re-read,
            // and let this tab's guards know too.
            const { data: again } = await supabase
              .from('profiles')
              .select('account_status')
              .eq('id', user.id)
              .single();
            if (again?.account_status === 'active') {
              notifyAccountStatusChanged();
              toast({ title: 'Welcome back!', description: 'Your account is active again.' });
              return;
            }
            toast({
              title: 'Account inactive',
              description: 'Your account is inactive. Please contact support to request reactivation.',
              variant: 'destructive',
            });
            return;
          }
          // Route guards cached 'deactivated' if they read the row before
          // this landed: tell them to read it again (Codex on #1300).
          notifyAccountStatusChanged();
          if (user.email) {
            try {
              await supabase.functions.invoke('send-account-notification', {
                body: {
                  email: user.email,
                  firstName: profile.first_name || 'User',
                  lastName: profile.last_name || '',
                  notificationType: 'reactivation',
                },
              });
            } catch (emailError) {
              console.error('Failed to send reactivation email:', emailError);
            }
          }
          toast({ title: 'Welcome back!', description: 'Your account is active again.' });
          return;
        }

        // Check if account is deleted
        if (profile?.account_status === 'deleted') {
          toast({
            title: "Account deleted",
            description: "This account has been permanently deleted.",
            variant: "destructive"
          });
          await supabase.auth.signOut();
        }
      } catch (error) {
        console.error('Error in account reactivation check:', error);
      }
    };

    checkAndReactivateAccount();
  }, [user, toast]);
}
